import React, { useState, useEffect, useRef } from "react";
import {
  Card,
  Grid,
  InputAdornment,
  Icon,
  TableContainer,
  Paper,
  Table,
  TableBody,
  TableRow,
  TableCell,
} from "@mui/material";
import MDInput from "components/MDInput";
import Button from "@mui/material/Button";
import PrintIcon from "@mui/icons-material/Print";
import TambahInboisButton from "components/STButton/TambahInboisButton";
import InboisBakiAwalButton from "components/STButton/InboisBakiAwalButton";
import InboisPelbagaiButton from "components/STButton/InboisPelbagaiButton";
import TambahInboisBakiAwal from "components/STPopup/TambahInboisBakiAwal";
import TambahInbois from "components/STPopup/TambahInbois";
import TambahInboisPelbagai from "components/STPopup/TambahInboisPelbagai";
import ViewInboisPelbagai from "components/STPopup/ViewInboisPelbagai";
import { apiGetInboisByNoInbois } from "api/inbois";
import { apiGetInboisDetailByNoInbois } from "api/inbois_detail";
import { apiSearchAkaun } from "api/akaun";
import { apiGetInboisByNoAkaun } from "api/inbois";
import { apiUpdateInbois } from "api/inbois";
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import { pageHeaderStyles } from "utils/pageHeaderStyles";
import { buttonStyles } from "utils/buttonStyles";
import { tableStyles } from "utils/tableStyles";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import DataTableHeadCell from "../examples/Tables/DataTable/DataTableHeadCell";
import { autoCapitalizePayload } from "utils/autoCapitalize";
import { printInboisPreview } from "components/STPrint/PrintInbois";
import { useSnackbar } from "components/GlobalSnackbar";
import { useConfirmDialog } from "components/ConfirmDialog";
import { handleApiError } from "utils/errorHandler";

export default function JanaInbois() {
  const { showSuccess, showError, showInfo, showWarning } = useSnackbar();
  const { confirmDelete, showConfirm } = useConfirmDialog();
  const [openBakiAwal, setOpenBakiAwal] = useState(false);
  const [openTambahInbois, setOpenTambahInbois] = useState(false);
  const [openPelbagai, setOpenPelbagai] = useState(false);
  const [openViewPelbagai, setOpenViewPelbagai] = useState(false);
  const [viewNoInbois, setViewNoInbois] = useState("");
  const [editInboisData, setEditInboisData] = useState(null); // for autofill edit mode
  const [noAkaun, setNoAkaun] = useState("");
  const [akaunData, setAkaunData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selectedAkaun, setSelectedAkaun] = useState(null);
  const [inboisData, setInboisData] = useState([]);
  const [loadingInbois, setLoadingInbois] = useState(false);
  const [selectedInboisIds, setSelectedInboisIds] = useState([]);
  const debounceRef = useRef();

  useEffect(() => {
    if (!noAkaun) {
      setAkaunData([]);
      setLoading(false);
      return;
    }
    setLoading(true);
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(async () => {
      try {
        const result = await apiSearchAkaun({ no_akaun: noAkaun }, 1, 10);
        setAkaunData(result.data || []);
      } catch (e) {
        setAkaunData([]);
      } finally {
        setLoading(false);
      }
    }, 400);
    return () => clearTimeout(debounceRef.current);
  }, [noAkaun]);

  // Fetch inbois data when selectedAkaun changes
  useEffect(() => {
    if (!selectedAkaun) {
      setInboisData([]);
      setSelectedInboisIds([]);
      return;
    }
    setLoadingInbois(true);
    apiGetInboisByNoAkaun(selectedAkaun.noakaun || selectedAkaun.no_akaun)
      .then((result) => {
        // Sort by tarikh_inbois ascending
        const sorted = (result.data || [])
          .slice()
          .sort((a, b) => new Date(a.tarikh_inbois) - new Date(b.tarikh_inbois));
        setInboisData(sorted);
        setSelectedInboisIds([]);
      })
      .catch(() => {
        setInboisData([]);
        setSelectedInboisIds([]);
      })
      .finally(() => setLoadingInbois(false));
  }, [selectedAkaun]);

  // Handler for Print button
  const handlePrintInbois = async () => {
    if (selectedInboisIds.length !== 1) {
      showInfo("Sila pilih satu rekod inbois untuk dicetak");
      return;
    }
    const selected = inboisData.find(
      (x) => (x.id ?? x.no_inbois) === selectedInboisIds[0]
    );
    if (!selected) {
      showError("Rekod inbois tidak dijumpai");
      return;
    }
    try {
      await printInboisPreview(selected);
    } catch (err) {
      showError("Gagal memuatkan maklumat inbois untuk cetakan");
    }
  };

  // Handler for Post button
  const handlePostInbois = async () => {
    // Get all checked rows
    const checkedRows = inboisData.filter((row) =>
      selectedInboisIds.includes(row.id ?? row.no_inbois)
    );
    if (checkedRows.length === 0) return;

    // If any checked row is NOT Perkara = Open Inbois (Pelbagai), show error and return
    const hasNonPelbagai = checkedRows.some((row) => row.perkara !== "Open Inbois (Pelbagai)");
    if (hasNonPelbagai) {
      showInfo("Sila pilih Open Inbois (Pelbagai) untuk di post");
      return;
    }

    // If any checked Open Inbois (Pelbagai) already posted, show error and return
    const alreadyPosted = checkedRows.some(
      (row) => row.perkara === "Open Inbois (Pelbagai)" && row.mark_post === 1
    );
    if (alreadyPosted) {
      showInfo("Open Inbois (Pelbagai) telah di post");
      return;
    }

    // Only affect checked rows with Perkara = 'Open Inbois (Pelbagai)' and mark_post = 0
    let toPost = checkedRows.filter(
      (row) => row.perkara === "Open Inbois (Pelbagai)" && row.mark_post === 0
    );
    // Guard by tarikh_tamat if available on selectedAkaun
    const tamat = selectedAkaun?.tarikh_tamat;
    if (tamat) {
      const over = toPost.filter(r => r.tarikh_inbois && new Date(r.tarikh_inbois) > new Date(tamat));
      if (over.length > 0) {
        showWarning(`Tarikh tamat sewaan adalah pada ${new Date(tamat).toLocaleDateString('en-CA')}. Terdapat ${over.length} rekod melebihi tarikh tamat dan tidak akan dipost.`);
        toPost = toPost.filter(r => !r.tarikh_inbois || new Date(r.tarikh_inbois) <= new Date(tamat));
      }
    }
    if (toPost.length === 0) {
      setSnackbar({ open: true, message: "Tiada rekod yang layak untuk Post.", severity: "info" });
      return;
    }
    try {
      // Import apiTambahLejar dynamically to avoid circular deps
      const { apiTambahLejar } = await import("api/lejar");
      const user = JSON.parse(localStorage.getItem("user") || "{}");
      for (const row of toPost) {
        await apiUpdateInbois(row.id, { mark_post: 1 });
        // Save to lejar table using row data
        const lejarPayload = {
          no_akaun: row.no_akaun,
          kod_harta: row.kod_harta || "",
          tarikh_inbois: row.tarikh_inbois,
          no_inbois: row.no_inbois,
          tarikh_resit: row.tarikh_inbois,
          no_resit: row.no_inbois,
          doc_date: row.tarikh_inbois ? row.tarikh_inbois.slice(0, 10) : "",
          doc_date1: row.tarikh_inbois,
          doc_no: row.no_inbois,
          perkara: row.perkara || "Open Inbois (Pelbagai)",
          debit: parseFloat(row.jumlah),
          kredit: 0.0,
          baki: 0.0,
          baki_asal: 0.0,
          id_pengguna: (user.id_pengguna || "").toLowerCase(),
          tarikh_simpan: row.tarikh_inbois,
          ageing: 0,
          mark_delete: 0,
          bahagian_pemantau: user.bahagian || "",
        };
        const capLejarPayload = autoCapitalizePayload(lejarPayload, ["emel", "email", "e_mail"]);
        try {
          await apiTambahLejar(capLejarPayload);
        } catch (err) {
          console.error("Failed to save lejar for row:", row, err);
          showError(
            "Gagal simpan ke lejar untuk no_inbois: " + row.no_inbois + "\n" + (err?.message || err)
          );
        }
      }
      showSuccess("Berjaya Post inbois terpilih");
      // Refresh table
      if (selectedAkaun) {
        setLoadingInbois(true);
        const result = await apiGetInboisByNoAkaun(selectedAkaun.noakaun || selectedAkaun.no_akaun);
        const sorted = (result.data || [])
          .slice()
          .sort((a, b) => new Date(a.tarikh_inbois) - new Date(b.tarikh_inbois));
        setInboisData(sorted);
        setSelectedInboisIds([]);
        setLoadingInbois(false);
      }
    } catch (e) {
      showError("Gagal Post inbois. Sila cuba lagi.");
    }
  };

  // Checkbox handlers
  const handleSelectInbois = (key) => {
    setSelectedInboisIds((prev) =>
      prev.includes(key) ? prev.filter((x) => x !== key) : [...prev, key]
    );
  };

  const handleSelectAllInbois = (event) => {
    if (event.target.checked) {
      setSelectedInboisIds(inboisData.map((inbois) => inbois.id ?? inbois.no_inbois));
    } else {
      setSelectedInboisIds([]);
    }
  };

  const handleDeleteSelectedInbois = async () => {
    if (selectedInboisIds.length === 0) {
      showInfo("Sila pilih rekod inbois untuk dipadam");
      return;
    }

    const confirmed = await showConfirm({
      title: `Padam ${selectedInboisIds.length} Rekod Inbois`,
      message: `Adakah anda pasti untuk memadam ${selectedInboisIds.length} rekod inbois? Tindakan ini tidak boleh dibatalkan.`,
      confirmText: "Padam",
      type: "danger"
    });

    if (!confirmed) return;

    try {
      setLoadingInbois(true);
      // Get the no_inbois for each selected id
      const selectedRows = inboisData.filter((row) =>
        selectedInboisIds.includes(row.id ?? row.no_inbois)
      );
      const noInboisList = selectedRows.map((row) => row.no_inbois).filter(Boolean);
      // Import lejar API and delete from lejar for each no_inbois
      const { apiDeleteLejarByNoInbois } = await import("api/lejar");
      for (const no_inbois of noInboisList) {
        try {
          await apiDeleteLejarByNoInbois(no_inbois);
        } catch (e) {
          // Optionally handle error for each lejar delete
          // Continue deleting others
        }
      }
      // Now delete from inbois table
      const { apiHapusInbois } = await import("api/inbois");
      await apiHapusInbois(selectedInboisIds);
      showSuccess(`${selectedInboisIds.length} rekod inbois berjaya dipadam`);
      // Refresh table after delete
      if (selectedAkaun) {
        const { apiGetInboisByNoAkaun } = await import("api/inbois");
        apiGetInboisByNoAkaun(selectedAkaun.noakaun || selectedAkaun.no_akaun)
          .then((result) => {
            const sorted = (result.data || [])
              .slice()
              .sort((a, b) => new Date(a.tarikh_inbois) - new Date(b.tarikh_inbois));
            setInboisData(sorted);
            setSelectedInboisIds([]);
          })
          .catch(() => {
            setInboisData([]);
            setSelectedInboisIds([]);
          })
          .finally(() => setLoadingInbois(false));
      } else {
        setLoadingInbois(false);
      }
    } catch (err) {
      handleApiError(err, showError);
      setLoadingInbois(false);
    }
  };

  // Handler to refresh inbois after popup success (BakiAwal or Pelbagai)
  const handleBakiAwalSuccess = () => {
    if (selectedAkaun) {
      setLoadingInbois(true);
      apiGetInboisByNoAkaun(selectedAkaun.noakaun || selectedAkaun.no_akaun)
        .then((result) => {
          const sorted = (result.data || [])
            .slice()
            .sort((a, b) => new Date(a.tarikh_inbois) - new Date(b.tarikh_inbois));
          setInboisData(sorted);
          setSelectedInboisIds([]);
        })
        .catch(() => {
          setInboisData([]);
          setSelectedInboisIds([]);
        })
        .finally(() => setLoadingInbois(false));
    }
  };

  // Handler for Pelbagai success (refresh + close popup)
  const handlePelbagaiSuccess = () => {
    handleBakiAwalSuccess();
    setOpenPelbagai(false);
  };

  // Handler for Butiran Pelbagai button
  const handleViewPelbagai = async () => {
    if (selectedInboisIds.length !== 1) {
      setSnackbar({ open: true, message: "Sila pilih SATU inbois sahaja.", severity: "warning" });
      return;
    }
    const selected = inboisData.find(
      (x) => (x.id ?? x.no_inbois) === selectedInboisIds[0]
    );
    if (!selected || selected.perkara !== "Open Inbois (Pelbagai)") {
      setSnackbar({
        open: true,
        message: "Hanya inbois dengan perkara 'Open Inbois (Pelbagai)' dibenarkan.",
        severity: "warning",
      });
      return;
    }
    // Fetch full inbois data by no_inbois
    let inboisRes;
    try {
      inboisRes = await apiGetInboisByNoInbois(selected.no_inbois);
    } catch {
      setSnackbar({ open: true, message: "Gagal mendapatkan maklumat inbois.", severity: "error" });
      return;
    }
    const inbois = inboisRes?.data;
    if (!inbois) {
      setSnackbar({ open: true, message: "Inbois tidak dijumpai.", severity: "error" });
      return;
    }
    if (inbois.mark_post === 1) {
      setViewNoInbois(selected.no_inbois);
      setOpenViewPelbagai(true);
      setEditInboisData(null);
    } else {
      // mark_post === 0, open editable popup with autofill
      let details = [];
      try {
        const detailRes = await apiGetInboisDetailByNoInbois(selected.no_inbois);
        details = detailRes.data || [];
      } catch {}
      setEditInboisData({ header: inbois, details });
      setOpenPelbagai(true);
      setViewNoInbois("");
    }
  };

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <MDBox p={2}>
        <Grid container spacing={2} alignItems="flex-start">
          {/* Search Box */}
          <Grid item xs={12} md={3}>
            <Card sx={{ width: "100%", mb: 2 }}>
                  <MDBox
                    mx={2}
                    mt={-3}
                    py={2}
                    px={2}
                    variant="gradient"
                    sx={pageHeaderStyles.success}
                    borderRadius="lg"
                    coloredShadow="success"
                  >
                  <MDTypography variant="h6" color="white">
                    Jana Inbois
                  </MDTypography>
                </MDBox>
              <MDBox px={3} pt={3} pb={1.5}>
                <Grid container spacing={2} alignItems="center">
                  <Grid item xs={12}>
                    <MDInput
                      fullWidth
                      label="No Akaun"
                      value={noAkaun}
                      onChange={(e) => {
                        setNoAkaun(e.target.value);
                        setSelectedAkaun(null); // Clear selection when typing
                        setInboisData([]); // Clear inbois table immediately
                        setSelectedInboisIds([]);
                      }}
                      InputLabelProps={{
                        sx: { color: "#888", fontWeight: 400 },
                      }}
                      InputProps={{
                        endAdornment: (
                          <InputAdornment position="end">
                            <Icon sx={{ cursor: "pointer" }}>search</Icon>
                          </InputAdornment>
                        ),
                      }}
                    />
                    {/* Pelbagai popup is rendered once at the bottom; removed duplicate here */}
                  </Grid>
                </Grid>
              </MDBox>
            </Card>
          </Grid>
          {/* Akaun Table Box */}
          <Grid item xs={14} md={9}>
            <Card sx={{ width: "100%", mb: 2 }}>
                  <MDBox
                    mx={2}
                    mt={-3}
                    py={2}
                    px={2}
                    variant="gradient"
                    sx={pageHeaderStyles.success}
                    borderRadius="lg"
                    coloredShadow="info"
                  >
                  <MDTypography variant="h6" color="white">
                    Senarai Penyewa
                  </MDTypography>
                </MDBox>
              <TableContainer component={Paper} sx={{ ...tableStyles.container, mt: 1 }}>
                <Table size="small">
                  <TableRow>
                    <DataTableHeadCell align="center" width="5%" sorted={false}></DataTableHeadCell>
                    <DataTableHeadCell p={1} align="center" width="30%" sorted={false}>
                      Nama
                    </DataTableHeadCell>
                    <DataTableHeadCell align="center" width="50%" sorted={false}>
                      Alamat
                    </DataTableHeadCell>
                    <DataTableHeadCell align="center" width="20%" sorted={false}>
                      Kod Harta
                    </DataTableHeadCell>
                  </TableRow>
                  <TableBody>
                    {(!noAkaun || akaunData.length === 0) && !loading && (
                      <TableRow>
                        <TableCell colSpan={4} align="center">
                          <span
                            style={{
                              color: "red",
                              fontWeight: 600,
                              fontSize: 16,
                              animation: "blinker 1s linear infinite",
                            }}
                          >
                            Tiada Pelanggan Tersenarai
                          </span>
                          <style>{`
                            @keyframes blinker {
                              50% { opacity: 0; }
                            }
                          `}</style>
                        </TableCell>
                      </TableRow>
                    )}
                    {loading && (
                      <TableRow>
                        <TableCell colSpan={4} align="center">
                          <MDTypography variant="body2" color="text.secondary" fontWeight="medium">
                            Mencari...
                          </MDTypography>
                        </TableCell>
                      </TableRow>
                    )}
                    {akaunData.length > 0 && !loading && (
                      (selectedAkaun
                        ? akaunData
                            .filter(
                              (akaun) =>
                                (akaun.noakaun || akaun.no_akaun) ===
                                (selectedAkaun.noakaun || selectedAkaun.no_akaun)
                            )
                        : akaunData
                      ).map((akaun, index) => {
                        const nama = akaun.nama || "-";
                        const alamat = [
                          akaun.alamat1_harta,
                          akaun.alamat2_harta,
                          akaun.poskod_harta,
                          akaun.bandar_harta,
                          akaun.negeri_harta,
                        ]
                          .filter(Boolean)
                          .join(", ");
                        const kodHarta = akaun.kodharta || akaun.kod_harta || "-";
                        const noAkaunVal = akaun.noakaun || akaun.no_akaun || "-";
                        const isChecked =
                          selectedAkaun &&
                          (selectedAkaun.noakaun || selectedAkaun.no_akaun) === noAkaunVal;
                        return (
                          <TableRow key={kodHarta + index} hover>
                            <TableCell
                              align="center"
                              sx={{ fontSize: 12, minWidth: 0, width: "5%" }}
                            >
                              <input
                                type="checkbox"
                                checked={isChecked}
                                onChange={(e) => {
                                  if (isChecked) {
                                    setSelectedAkaun(null); // Uncheck: clear selection
                                  } else {
                                    setSelectedAkaun(akaun); // Check: select
                                  }
                                }}
                                style={{ cursor: "pointer" }}
                              />
                            </TableCell>
                            <TableCell
                              align="center"
                              sx={{ fontSize: 12, minWidth: 0, width: "30%", fontWeight: "bold" }}
                            >
                              {nama}
                            </TableCell>
                            <TableCell sx={{ fontSize: 12, minWidth: 0, width: "50%" }}>
                              {alamat}
                            </TableCell>
                            <TableCell
                              align="center"
                              sx={{ fontSize: 12, minWidth: 0, width: "20%" }}
                            >
                              {kodHarta}
                            </TableCell>
                          </TableRow>
                        );
                      })
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
            </Card>
          </Grid>
        </Grid>
        {/* Inbois Table in a separate Grid below */}
        {selectedAkaun && (
          <Grid container spacing={0} sx={{ mt: 3 }}>
            <Grid item xs={12}>
              <Card>
                <MDBox
                  display="flex"
                  alignItems="center"
                  justifyContent="space-between"
                  sx={{ px: 3, pt: 2, pb: 1 }}
                >
                  <MDTypography variant="h6">
                    Senarai Inbois untuk Akaun: {selectedAkaun.noakaun || selectedAkaun.no_akaun}
                  </MDTypography>
                </MDBox>
                <MDBox sx={{ px: 3, pb: 2 }}>
                  <Grid container alignItems="center">
                    <Grid
                      item
                      xs={12}
                      md={6}
                      lg={6}
                      sx={{ display: "flex", justifyContent: "flex-start", gap: 1, pt: 1 }}
                    >
                      <InboisBakiAwalButton
                        onClick={() => setOpenBakiAwal(true)}
                        disabled={loadingInbois}
                      />
                      <TambahInboisButton
                        onClick={() => setOpenTambahInbois(true)}
                        disabled={loadingInbois}
                      />
                    </Grid>
                    <Grid
                      item
                      xs={12}
                      md={6}
                      lg={6}
                      sx={{ display: "flex", justifyContent: "flex-end", gap: 1, pt: 1 }}
                    >
                      <InboisPelbagaiButton
                        onClick={() => {
                          setEditInboisData(null); // ensure add mode
                          setOpenPelbagai(true);
                        }}
                        disabled={loadingInbois}
                      />
                      <Button
                        variant="contained"
                        size="small"
                        onClick={handleViewPelbagai}
                        disabled={loadingInbois}
                        sx={{ ...buttonStyles.save, mr: 1 }}
                      >
                        Butiran Pelbagai
                      </Button>
                      <Button
                        variant="contained"
                        size="small"
                        sx={{ ...buttonStyles.primary, mr: 1 }}
                        onClick={handlePostInbois}
                        disabled={loadingInbois || selectedInboisIds.length === 0}
                      >
                        Post
                      </Button>
                    </Grid>
                  </Grid>
                </MDBox>
                <TableContainer component={Paper} sx={{ ...tableStyles.container, mt: 2 }}>
                  <Table size="small">
                    <TableBody>
                      {/* Header row */}
                      <TableRow>
                        <TableCell align="center">
                          <MDTypography variant="caption" fontWeight="bold">
                            {/* No */}
                          </MDTypography>
                        </TableCell>
                        <TableCell padding="checkbox">
                          <MDTypography variant="caption" fontWeight="bold">
                            {/* Checkbox */}
                          </MDTypography>
                        </TableCell>
                        <TableCell align="center">
                          <MDTypography variant="caption" fontWeight="bold">
                            No Akaun
                          </MDTypography>
                        </TableCell>
                        <TableCell align="center">
                          <MDTypography variant="caption" fontWeight="bold">
                            Tarikh Inbois
                          </MDTypography>
                        </TableCell>
                        <TableCell align="center">
                          <MDTypography variant="caption" fontWeight="bold">
                            No Inbois
                          </MDTypography>
                        </TableCell>
                        <TableCell align="center">
                          <MDTypography variant="caption" fontWeight="bold">
                            Perkara
                          </MDTypography>
                        </TableCell>
                        <TableCell align="center">
                          <MDTypography variant="caption" fontWeight="bold">
                            Jumlah
                          </MDTypography>
                        </TableCell>
                        <TableCell align="center">
                          <MDTypography variant="caption" fontWeight="bold">
                            Status Post
                          </MDTypography>
                        </TableCell>
                      </TableRow>
                      {/* Data rows */}
                      {loadingInbois && (
                        <TableRow>
                          <TableCell colSpan={7} align="center">
                            <MDTypography
                              variant="body2"
                              color="text.secondary"
                              fontWeight="medium"
                            >
                              Memuatkan inbois...
                            </MDTypography>
                          </TableCell>
                        </TableRow>
                      )}
                      {!loadingInbois && inboisData.length === 0 && (
                        <TableRow>
                          <TableCell colSpan={7} align="center">
                            <MDTypography variant="body2" color="text">
                              Tiada rekod inbois dijumpai untuk akaun ini.
                            </MDTypography>
                          </TableCell>
                        </TableRow>
                      )}
                      {!loadingInbois &&
                        inboisData.map((inbois, idx) => (
                            <TableRow key={inbois.id || inbois.no_inbois || idx} hover>
                            <TableCell align="center">
                              <MDTypography variant="caption" fontWeight="regular">
                                {idx + 1}
                              </MDTypography>
                            </TableCell>
                            <TableCell padding="checkbox">
                              <input
                                type="checkbox"
                                checked={selectedInboisIds.includes(inbois.id ?? inbois.no_inbois)}
                                onChange={() => handleSelectInbois(inbois.id ?? inbois.no_inbois)}
                                style={{ cursor: "pointer" }}
                              />
                            </TableCell>
                            <TableCell align="center">
                              <MDTypography variant="caption" fontWeight="regular">
                                {inbois.no_akaun || "N/A"}
                              </MDTypography>
                            </TableCell>
                            <TableCell align="center">
                              <MDTypography variant="caption" fontWeight="regular">
                                {inbois.tarikh_inbois
                                  ? new Date(inbois.tarikh_inbois).toLocaleDateString("en-CA")
                                  : "N/A"}
                              </MDTypography>
                            </TableCell>
                            <TableCell align="center">
                              <MDTypography variant="caption" fontWeight="regular">
                                {inbois.no_inbois || "N/A"}
                              </MDTypography>
                            </TableCell>
                            <TableCell>
                              <MDTypography variant="caption" fontWeight="regular">
                                {inbois.perkara || "N/A"}
                              </MDTypography>
                            </TableCell>
                            <TableCell align="right">
                              <MDTypography variant="caption" fontWeight="regular">
                                {Number(inbois.jumlah || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                              </MDTypography>
                            </TableCell>
                            <TableCell align="center">
                              <MDTypography variant="caption" fontWeight="regular">
                                {inbois.mark_post || "0"}
                              </MDTypography>
                            </TableCell>
                          </TableRow>
                        ))}
                    </TableBody>
                  </Table>
                </TableContainer>
                <Grid sx={{ px: 3, pt: 2, pb: 1 }} display="flex" gap={2}>
                  <Button
                    variant="contained"
                    size="small"
                    onClick={handleDeleteSelectedInbois}
                    disabled={selectedInboisIds.length === 0 || loadingInbois}
                    style={{
                      background: 'linear-gradient(0deg,rgba(99, 0, 0, 1) 0%,rgba(206, 0, 0, 1) 100%)',
                      color: '#fff',
                    }}
                    sx={{
                      background: "#d32f2f",
                      color: selectedInboisIds.length > 0 ? "#ffffffff" : undefined,
                      cursor:
                        selectedInboisIds.length === 0 || loadingInbois ? "not-allowed" : "pointer",
                      fontWeight: 600,
                    }}
                  >
                    Hapus ({selectedInboisIds.length})
                  </Button>
                  <Button
                    variant="contained"
                    startIcon={<PrintIcon />}
                    size="small"
                    sx={buttonStyles.primary}
                    onClick={handlePrintInbois}
                    disabled={loadingInbois || selectedInboisIds.length === 0}
                  >
                    Cetak Inbois
                  </Button>
                </Grid>
              </Card>
            </Grid>
          </Grid>
        )}
        {/* TambahInboisBakiAwal Popup */}
        <TambahInboisBakiAwal
          open={openBakiAwal}
          onClose={() => setOpenBakiAwal(false)}
          selectedAkaun={selectedAkaun}
          onSuccess={handleBakiAwalSuccess}
        />
        {/* TambahInbois Popup */}
        <TambahInbois
          open={openTambahInbois}
          onClose={() => setOpenTambahInbois(false)}
          selectedAkaun={selectedAkaun}
          onSuccess={() => {
            setOpenTambahInbois(false);
            // Optionally refresh inbois list after success
            if (typeof selectedAkaun === "object" && selectedAkaun) {
              // re-fetch inbois for selectedAkaun
              apiGetInboisByNoAkaun(selectedAkaun.noakaun || selectedAkaun.no_akaun)
                .then((result) => {
                  const sorted = (result.data || [])
                    .slice()
                    .sort((a, b) => new Date(a.tarikh_inbois) - new Date(b.tarikh_inbois));
                  setInboisData(sorted);
                  setSelectedInboisIds([]);
                })
                .catch(() => {
                  setInboisData([]);
                  setSelectedInboisIds([]);
                });
            }
          }}
        />
        {/* TambahInboisPelbagai Popup */}
        <TambahInboisPelbagai
          open={openPelbagai}
          onClose={() => {
            setOpenPelbagai(false);
            setEditInboisData(null);
          }}
          selectedAkaun={selectedAkaun}
          onSubmit={() => {
            setOpenPelbagai(false);
            setEditInboisData(null);
          }}
          onSuccess={handlePelbagaiSuccess}
          autofillData={editInboisData}
        />
        {/* ViewInboisPelbagai Popup */}
        <ViewInboisPelbagai
          open={openViewPelbagai}
          onClose={() => setOpenViewPelbagai(false)}
          noInbois={viewNoInbois}
        />
      </MDBox>
    </DashboardLayout>
  );
}
